import { ApiError, buildApiUrl } from "@jobenvoy/core";
import { SAMPLE_DATA } from "@/config/env";

/**
 * Where the access token comes from, when there is one. The sign-in gate
 * registers AuthKit's getter once the person is signed in; until then, and
 * for an API in its local auth mode, there is nothing to send and no
 * header goes out. Kept here so the header is set in exactly one place.
 */
type AccessTokenProvider = () => Promise<string | null | undefined>;

let accessTokenProvider: AccessTokenProvider | null = null;

export function setAccessTokenProvider(provider: AccessTokenProvider | null): void {
    accessTokenProvider = provider;
}

async function authorization(): Promise<Record<string, string>> {
    if (!accessTokenProvider) {
        return {};
    }
    let token: string | null | undefined;
    try {
        token = await accessTokenProvider();
    } catch (error) {
        // AuthKit could not refresh the session ("No access token
        // available"). The session is gone as surely as on a 401: send the
        // person to sign in, and refuse this request as the API would.
        console.warn("The access token could not be refreshed", error);
        tellUnauthorized();
        throw new ApiError("Request failed (401): the session has ended", 401, "");
    }
    return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Every JSON call: the path joined to the gateway's base, with the token
 * unless `withToken` is false.
 */
async function request<T>(path: string, init: RequestInit, withToken = true): Promise<T> {
    if (SAMPLE_DATA) {
        // Placeholder data, answered in the page. Loaded on demand so the
        // sample never ships in a build that talks to the API.
        const { sampleRequest } = await import("@/sample/server");
        return sampleRequest<T>(
            init.method ?? "GET",
            path,
            init.body ? JSON.parse(String(init.body)) : undefined,
        );
    }

    const response = await fetch(buildApiUrl(path), {
        ...init,
        headers: {
            "Content-Type": "application/json",
            ...(withToken ? await authorization() : {}),
            ...init.headers,
        },
    });

    await answered(response);
    return jsonOf<T>(response);
}

/**
 * Who to tell when the API says the session is gone (401): the sign-in,
 * registered with the token getter. Told at most once per page, since
 * every request in flight hears the same 401.
 */
let unauthorizedHandler: (() => void) | null = null;
let toldUnauthorized = false;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
    unauthorizedHandler = handler;
    toldUnauthorized = false;
}

function tellUnauthorized(): void {
    if (unauthorizedHandler && !toldUnauthorized) {
        toldUnauthorized = true;
        unauthorizedHandler();
    }
}

/** Throws the API's refusal, with its body, for an answer that is not a success. */
async function answered(response: Response): Promise<void> {
    if (!response.ok) {
        const body = await response.text();
        if (response.status === 401) tellUnauthorized();
        throw new ApiError(`Request failed (${response.status}): ${body}`, response.status, body);
    }
}

/**
 * A success's JSON. Nothing for a 204 or an empty body; a refusal of its
 * own for a body that is not JSON, such as a proxy's HTML page, rather
 * than the parser's "Unexpected token '<'", and for JSON that is not an
 * object, such as `null`.
 */
async function jsonOf<T>(response: Response): Promise<T> {
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    if (!text.trim()) return undefined as T;
    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch {
        throw new ApiError(
            `Request failed (${response.status}): the answer was not JSON`,
            response.status,
            "",
        );
    }
    // Every success a service answers with JSON is an object. A null, a
    // list or a bare value is not an answer to read fields from.
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new ApiError(
            `Request failed (${response.status}): the answer was not an object`,
            response.status,
            "",
        );
    }
    return parsed as T;
}

/**
 * One path segment from an id: encoded, and never `.` or `..`, which a
 * URL would read as "this directory" or "the one above" and send the
 * request to another route. Refused here, before anything is sent.
 */
export function pathSegment(value: string): string {
    if (value === "" || value === "." || value === "..") {
        throw new ApiError(`Request refused: "${value}" is not an id`, 400, "That is not an id.");
    }
    try {
        return encodeURIComponent(value);
    } catch {
        // A lone surrogate is no text at all, and no URL can carry it.
        throw new ApiError("Request refused: the id is not text", 400, "That is not an id.");
    }
}

/**
 * A file sent as the body itself, for a route that takes the bytes
 * (`application/octet-stream`). The sample is handed the file's size, its
 * type and, for a text file, its text, never the bytes.
 */
export async function postFile<T>(path: string, file: Blob): Promise<T> {
    if (SAMPLE_DATA) {
        const { sampleRequest } = await import("@/sample/server");
        const text = file.type.startsWith("application/pdf") ? "" : await file.text();
        return sampleRequest<T>("POST", path, {
            sample_file: { size: file.size, type: file.type, text },
        });
    }
    const response = await fetch(buildApiUrl(path), {
        method: "POST",
        body: file,
        headers: { "Content-Type": "application/octet-stream", ...(await authorization()) },
    });
    await answered(response);
    return jsonOf<T>(response);
}

/** A route that answers with a file, a PDF, as a blob. With `body`, it is a POST of that JSON. */
export async function fetchBlob(path: string, body?: unknown): Promise<Blob> {
    const method = body === undefined ? "GET" : "POST";
    if (SAMPLE_DATA) {
        const { sampleBlob } = await import("@/sample/server");
        return sampleBlob(method, path, body);
    }
    const response = await fetch(buildApiUrl(path), {
        method,
        body: body === undefined ? undefined : JSON.stringify(body),
        headers: { "Content-Type": "application/json", ...(await authorization()) },
    });
    await answered(response);
    // A page served in place of the file, such as the app's own index.html
    // from a host that does not route /v1, is not the file.
    if ((response.headers.get("Content-Type") ?? "").startsWith("text/html")) {
        throw new ApiError(
            `Request failed (${response.status}): the answer was a page, not a file`,
            response.status,
            "",
        );
    }
    return response.blob();
}

export function getJson<T>(path: string): Promise<T> {
    return request<T>(path, { method: "GET" });
}

export function postJson<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, { method: "POST", body: JSON.stringify(body) });
}

/**
 * A POST with no token, for a route open to anyone, such as the contact
 * form. Its service allows no Authorization header across origins, so a
 * token would fail the browser's preflight and the request with it.
 */
export function postJsonWithoutToken<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, { method: "POST", body: JSON.stringify(body) }, false);
}

/** A POST of nothing: an action whose route takes no body, such as unpausing. */
export function postEmpty<T>(path: string): Promise<T> {
    return request<T>(path, { method: "POST" });
}

export function putJson<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, { method: "PUT", body: JSON.stringify(body) });
}

/** A PUT of nothing: a route that names everything in its path, such as saving a posting. */
export function putEmpty<T>(path: string): Promise<T> {
    return request<T>(path, { method: "PUT" });
}

export function deleteJson<T>(path: string): Promise<T> {
    return request<T>(path, { method: "DELETE" });
}

/** A value worth sending: not absent, not empty, and not a number that is not one. */
function isPresent(value: unknown): boolean {
    if (value === null || value === undefined || value === "") return false;
    return typeof value !== "number" || Number.isFinite(value);
}

/**
 * A query string from named values, in the order given, leaving out
 * what is absent or empty, and a number that is NaN or infinite. A list
 * is given once a value, as the services read it: `title=a&title=b`, its
 * absent or empty items left out too.
 */
export function queryString(
    params: Record<string, string | number | boolean | readonly string[] | null | undefined>,
): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (Array.isArray(value)) {
            for (const item of value as readonly unknown[]) {
                if (isPresent(item)) search.append(key, String(item));
            }
        } else if (isPresent(value)) {
            search.set(key, String(value));
        }
    }
    const text = search.toString();
    return text ? `?${text}` : "";
}

/** The sentence a refusal's JSON carries: itself, or its `message`, `error` or first text field. */
function sentenceIn(parsed: unknown): string | null {
    const sentence = (value: unknown) =>
        typeof value === "string" && value.trim() ? value.trim() : null;
    if (typeof parsed === "string") return sentence(parsed);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    const record = parsed as Record<string, unknown>;
    return (
        sentence(record.message) ??
        sentence(record.error) ??
        Object.values(record)
            .map(sentence)
            .find((value) => value !== null) ??
        null
    );
}

/** The longest refusal shown as it was written. */
const MAX_MESSAGE = 300;

/** The message a person can read out of an error, API or otherwise. */
const NETWORK_WORDS =
    /^(failed to fetch|networkerror when attempting to fetch resource\.?|load failed|network request failed)$/i;

export function describeError(error: unknown): string {
    if (error instanceof ApiError) {
        // The API answers a refused action with a sentence, as JSON or as
        // text. Either way the body is the explanation.
        const body = error.body.trim();
        // A proxy's or a CDN's error page, or a body too long to be a
        // sentence, is not something to show a person.
        if (body.startsWith("<") || body.length > MAX_MESSAGE) {
            return `The service answered ${error.status}. Try again in a moment.`;
        }
        const fallback = `The API answered ${error.status}.`;
        if (!body) return fallback;
        let parsed: unknown;
        try {
            parsed = JSON.parse(body);
        } catch {
            // Not JSON: the body is the message.
            return body;
        }
        // JSON: a sentence in it, or none to show. A bare `null`, a number
        // or an object of codes is not something to put before a person.
        return sentenceIn(parsed) ?? fallback;
    }
    // A fetch that never reached the service: each browser has its own
    // words for it ("Failed to fetch", "NetworkError ...", "Load failed").
    if (error instanceof TypeError && NETWORK_WORDS.test(error.message)) {
        return "Job Envoy could not be reached. Check your connection and try again.";
    }
    if (error instanceof Error) {
        return error.message;
    }
    return "Something went wrong.";
}
